import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Alert, Col, Form } from "react-bootstrap";
import Loader from "./Loader.js";
import NotyfContext from "../contexts/NotyfContext.js";
import useAuth from "../hooks/useAuth.js";
import { canEditEventReports, canManageStreaming } from "../utils/eventPermissions.js";
import { convertDateToDisplayA } from "../assets/functions.js";
import { StreamingLoadTestModal } from "./AutonomousStreamingLoadTestModal.js";
import {
    ScheduleStreamingChannelForm,
    ScheduleStreamingManagementComponent,
    ScheduleStreamingReportModal,
} from "./EventsComponentsTabs.js";

const parseScheduleJson = (value) => {
    if (!value) return {};
    if (typeof value === "object") return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return {};
    }
};

const getScheduleOptions = (rows) => (Array.isArray(rows) ? rows : [])
    .map((schedule) => {
        const json = parseScheduleJson(schedule.json);
        const value = Number(schedule.id);
        return {
            value,
            label: json.title || schedule.scheduleTitle || `Agenda #${value}`,
            startDate: convertDateToDisplayA(schedule.start_date || schedule.startDateFormatted, 0) || "",
        };
    })
    .filter((option) => Number.isInteger(option.value) && option.value > 0);

const getParticipantUsers = (response) => (response?.data?.participants || [])
    .filter((participant) => Number(participant.type) === 1);

export default function StreamingReportsSection({ dataTable, refreshTrigger, setDataTableUpdate, onLoadingChange }) {
    const notyf = useContext(NotyfContext);
    const userState = useAuth();
    const canEditReports = canEditEventReports(userState, dataTable?.platform);
    const canManageStreamings = canManageStreaming(userState, dataTable?.platform);
    const eventId = dataTable?.event_id || dataTable?.evento_id;
    const streamingDataTable = eventId && dataTable?.event_id !== eventId
        ? { ...dataTable, event_id: eventId }
        : dataTable;
    const [streamings, setStreamings] = useState(null);
    const [scheduleOptions, setScheduleOptions] = useState([]);
    const [dataAddForm, setDataAddForm] = useState({
        scheduleStreamings: [],
        availableStreamingUsers: [],
        deletedScheduleStreamings: [],
        streamingChannelData: false,
        streamingEditorMode: false,
        streamingReportData: false,
    });
    const [modal, setModal] = useState({
        showStreamingChannelForm: false,
        showStreamingReportModal: false,
        showStreamingLoadTestModal: false,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [localRefreshKey, setLocalRefreshKey] = useState(0);
    const [streamingLoadTestInProgress, setStreamingLoadTestInProgress] = useState(false);
    const [streamingLoadTestRefreshKey, setStreamingLoadTestRefreshKey] = useState(0);

    const refreshTransmissions = useCallback((parentUpdater) => {
        setLocalRefreshKey((current) => current + 1);
        setDataTableUpdate?.(parentUpdater);
    }, [setDataTableUpdate]);

    useEffect(() => {
        if (!eventId) {
            setStreamings([]);
            setLoading(false);
            onLoadingChange?.("streamings", false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        onLoadingChange?.("streamings", true);
        setError("");

        const requests = [
            axios.get("/api/event/plataformaEventos/schedule-streaming", {
                params: { event_id: eventId },
            }),
            canEditReports
                ? axios.get("/api/event/plataformaEventos/component", {
                    params: { event_id: eventId, component_type: "agenda" },
                })
                : Promise.resolve(null),
            canEditReports
                ? axios.get(`/api/event/participants/${eventId}`, { params: { platform: 2 } })
                : Promise.resolve(null),
        ];

        Promise.allSettled(requests).then((results) => {
            if (!active) return;

            const streamingResult = results[0];
            if (streamingResult.status !== "fulfilled") {
                setStreamings([]);
                const message = streamingResult.reason?.response?.data?.message
                    || "Não foi possível carregar as transmissões.";
                setError(message);
                notyf.open({ type: "danger", message, ripple: true, dismissible: true });
            } else {
                const rows = Array.isArray(streamingResult.value?.data?.data)
                    ? streamingResult.value.data.data
                    : [];
                setStreamings(rows);
                setDataAddForm((current) => ({ ...current, scheduleStreamings: rows }));
            }

            const scheduleResult = results[1];
            if (scheduleResult?.status === "fulfilled") {
                setScheduleOptions(getScheduleOptions(scheduleResult.value?.data?.message));
            }

            const usersResult = results[2];
            if (usersResult?.status === "fulfilled") {
                setDataAddForm((current) => ({
                    ...current,
                    availableStreamingUsers: getParticipantUsers(usersResult.value),
                }));
            }

            setLoading(false);
            onLoadingChange?.("streamings", false);
        });

        return () => {
            active = false;
        };
    }, [canEditReports, eventId, localRefreshKey, notyf, onLoadingChange, refreshTrigger]);

    const handlePreviewError = useCallback(() => {}, []);

    return (
        <>
            <Form.Group as={Col} className="textHeaderAplicativo d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Lista de transmissões ({Array.isArray(streamings) ? streamings.length : 0})</h5>
            </Form.Group>
            <h6 className="text-muted mt-3">
                Consulte, compartilhe e gerencie as transmissões disponíveis no evento.
            </h6>

            {error && <Alert variant="danger" className="p-2">{error}</Alert>}
            {loading && streamings === null ? (
                <div className="py-5"><Loader /></div>
            ) : (
                <ScheduleStreamingManagementComponent
                    scheduleId={null}
                    dataTable={streamingDataTable}
                    dataAddForm={dataAddForm}
                    setDataAddForm={setDataAddForm}
                    setModal={setModal}
                    setDataTableUpdate={refreshTransmissions}
                    streamings={streamings || []}
                    emptyMessage="Nenhuma transmissão disponível"
                    globalMode
                    disabled={!canManageStreamings}
                />
            )}

            <ScheduleStreamingChannelForm
                modal={modal}
                setModal={setModal}
                dataTable={streamingDataTable}
                dataAddForm={dataAddForm}
                setDataAddForm={setDataAddForm}
                setDataTableUpdate={refreshTransmissions}
                handlePreviewError={handlePreviewError}
                scheduleOptions={scheduleOptions}
                availableStreamings={streamings || []}
            />
            <ScheduleStreamingReportModal
                modal={modal}
                setModal={setModal}
                dataTable={streamingDataTable}
                dataAddForm={dataAddForm}
                setDataAddForm={setDataAddForm}
                setDataTableUpdate={refreshTransmissions}
                isStreamingLoadTestInProgress={streamingLoadTestInProgress}
                streamingLoadTestRefreshKey={streamingLoadTestRefreshKey}
                setShowStreamingLoadTestModal={(show) => setModal((current) => ({ ...current, showStreamingLoadTestModal: show }))}
                canManage={canManageStreamings}
            />
            <StreamingLoadTestModal
                show={modal.showStreamingLoadTestModal}
                onHide={() => setModal((current) => ({ ...current, showStreamingLoadTestModal: false }))}
                origin="streamingReport"
                onTestRunningChange={setStreamingLoadTestInProgress}
                onTerminal={() => setStreamingLoadTestRefreshKey((current) => current + 1)}
                context={{
                    eventId,
                    scheduleId: dataAddForm.streamingReportData?.schedule_id,
                    streamingId: dataAddForm.streamingReportData?.id,
                }}
            />
        </>
    );
}
